import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import React, { useMemo, useState, useEffect } from 'react';

import { Box } from '@mui/system';
import Tab from '@mui/material/Tab';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import TabContext from '@mui/lab/TabContext';
import {
  Card,
  Grid,
  Table,
  Paper,
  Stack,
  Popper,
  Popover,
  MenuItem,
  useTheme,
  Typography,
  TableContainer,
  FormHelperText,
} from '@mui/material';

import useResponsiveScreen from 'src/hooks/useResponsiveScreen';

import { genericApiToaster } from 'src/services/utils';

import TableToolbar from 'src/components/ToolBars/TableToolbar';
import TableHeaders from 'src/components/tableHeaders/TableHeaders';
import ActionButton from 'src/components/action-button/action-button';
import TablePaginations from 'src/components/table-pagination/TablePaginations';

import AddShareableForm from 'src/sections/shareableForm/AddShareableForm';
import ShareableFormDetails from 'src/sections/shareableForm/ShareableFormDetails';
import TableBodyShareableForm from 'src/sections/shareableForm/TableBodyShareableForm';
import Surveydashboard from 'src/sections/shareableForm/surveyDashboard/SurveyDashboard';

import { LANGS } from './ShareableFormLang';
import { useAuth } from '../../hooks/useAuth';
import Iconify from '../../components/iconify';
import { useToast } from '../../hooks/useToast';
import { useModal, useConfirmModal } from '../../hooks/useModal';
import { TABLE_HEAD_FORMS_SHAREABLE } from './ShareableFormHeaders';
import { titleHeadingStyle, titleHeadingStyleSmole } from '../style';
import { getComparator, applySortFilter } from '../../utils/filterData';
import {
  deleteFormLink,
  activateFormLink,
  fetchAllFormsSharable,
  deleteMultipleFormLinks,
} from '../../services/form.link.service';

ShareableFormsPage.propTypes = {
  row: PropTypes.object,
};

function ShareableFormsPage({ row }) {
  const [shareForms, setShareForms] = useState([]);
  const [selectedFormId, setSelectedFormId] = useState(row ? row.form.id : '');

  const [activeTab, setActiveTab] = useState('1');

  const [formsShareable, setFormsShareable] = useState([]);
  const [selectedFormsShareable, setSelectedFormsShareable] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPageFormsShareable, setCurrentPageFormsShareable] = useState([]);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('id');

  // Aligned, multi-layered search & filter states
  const [searchValue, setSearchValue] = useState('');
  const [searchBy, setSearchBy] = useState('id'); // Default search target
  const [filterBy, setFilterBy] = useState(''); // Category filter type
  const [filterTitle, setFilterTitle] = useState(''); // Selected category value
  const [selectedFilter, setSelectedFilter] = useState('id');

  const [filterPopover, setFilterPopover] = useState(null);
  const [subFilterPopover, setSubFilterPopover] = useState({
    status: null,
    isMultiple: null,
    language: null,
    isAnonymous: null,
    isHidden: null, // Added to control survey/form type sub-popper
  });
  const [openShareableFormDetails, setOpenShareableFormDetails] = useState(false);
  const [isNotFound, setIsNotFound] = useState(false);
  const [popoverAnchorEl, setPopoverAnchorEl] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [indeterminateCheckbox, setIndeterminateCheckbox] = useState(false);
  const { showToast, hideToast } = useToast();
  const { showModal, hideModal } = useModal();
  const { permissions, departmentIds } = useAuth();
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { isSmallScreen } = useResponsiveScreen();

  const canCreate = permissions?.includes('SHAREABLE_FORM:CREATE');
  const canUpdate = permissions?.includes('SHAREABLE_FORM:UPDATE');
  const canDelete = permissions?.includes('SHAREABLE_FORM:DELETE');

  const emptyRows = useMemo(
    () => (page > 0 ? Math.max(0, (1 + page) * rowsPerPage - formsShareable.length) : 0),
    [page, rowsPerPage, formsShareable.length]
  );

  const isSelected = (id) => selectedFormsShareable.indexOf(id) !== -1;

  const handleFetchFormsSharableByGeneratedBy = async () => {
    genericApiToaster(
      fetchAllFormsSharable,
      [],
      '',
      'cannot load shareable form data',
      showToast,
      hideToast,
      t,
      setFormsShareable
    );
  };

  useEffect(() => {
    handleFetchFormsSharableByGeneratedBy();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpenMenu = (event, row) => {
    setPopoverAnchorEl(event.currentTarget);
    setSelectedItem(row);
  };

  const handleCloseMenu = () => {
    setPopoverAnchorEl(null);
  };

  useEffect(() => {
    if (activeTab === '2') {
      handleFetchForms();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const handleFetchForms = async () => {
    genericApiToaster(
      fetchAllFormsSharable,
      [],
      '',
      t('errorMessages.couldntLoadDep'),
      showToast,
      hideToast,
      t,
      setShareForms
    );
  };

  // Fetch forms on component mount
  useEffect(() => {
    handleFetchForms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectAllClick = (event) => {
    const newSelecteds = currentPageFormsShareable.map((n) => n.id);
    if (newSelecteds.every((id) => selectedFormsShareable.includes(id))) {
      setSelectedFormsShareable(
        selectedFormsShareable.filter((id) => !newSelecteds.includes(id))
      );
      return;
    }
    setSelectedFormsShareable([...new Set([...selectedFormsShareable, ...newSelecteds])]);
  };

  const handleClick = (event, id) => {
    const selectedIndex = selectedFormsShareable.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedFormsShareable, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedFormsShareable.slice(1));
    } else if (selectedIndex === selectedFormsShareable.length - 1) {
      newSelected = newSelected.concat(selectedFormsShareable.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedFormsShareable.slice(0, selectedIndex),
        selectedFormsShareable.slice(selectedIndex + 1)
      );
    }

    setSelectedFormsShareable(newSelected);
  };

  const filteredFormsShareable = useMemo(() => {
    // 1. Filter by Department first (original check)
    const baseData = formsShareable.filter((row) => {
      const deptId = row.form?.departmentId || row.form?.department?.id;
      return (departmentIds || []).includes(deptId);
    });

    // 2. Second: Apply your original, fully-functional sorting and free-text filtering utility
    let result = applySortFilter(
      baseData,
      getComparator(order, orderBy),
      searchValue, // Binds search text
      searchBy // Binds search field ('id', 'form.title', 'generatedBy.username')
    );

    // 3. Third: Apply the category filters as a clean post-filter on the result
    if (filterBy && filterTitle) {
      const targetVal = String(filterTitle).toLowerCase().trim();
      result = result.filter((row) => {
        if (filterBy === 'isActive') {
          return String(row.isActive) === targetVal;
        }
        if (filterBy === 'isMultipleSubmission') {
          return String(row.isMultipleSubmission) === targetVal;
        }
        if (filterBy === 'language') {
          return String(row.language).toLowerCase() === targetVal;
        }
        if (filterBy === 'isAnonymous') {
          return String(row.isAnonymous) === targetVal;
        }
        if (filterBy === 'isHidden') {
          return String(row.isHidden) === targetVal;
        }
        return true;
      });
    }

    setIsNotFound(!result.length && !!searchValue);
    return result;
  }, [
    searchValue,
    searchBy,
    filterBy,
    filterTitle,
    order,
    orderBy,
    formsShareable,
    departmentIds,
  ]);

  useEffect(() => {
    const startIdx = page * rowsPerPage;
    const endIdx = startIdx + rowsPerPage;
    setCurrentPageFormsShareable(filteredFormsShareable.slice(startIdx, endIdx));
  }, [page, rowsPerPage, filteredFormsShareable]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset page to 0 whenever rows per page changes
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const RemoveFormShareable = async (id) => {
    genericApiToaster(
      deleteFormLink,
      id,
      t('successMessages.deleteShareableFormSuccess'),
      t('errorMessages.deleteShareableFormError'),
      showToast,
      hideToast,
      t,
      () => {
        handleFetchFormsSharableByGeneratedBy();
        setSelectedFormsShareable([]);
      }
    );
  };

  const multipleFormShareableDelete = async (selected) => {
    genericApiToaster(
      deleteMultipleFormLinks,
      [selected],
      '',
      t('errorMessages.deleteShareableFormError'),
      showToast,
      hideToast,
      t,
      (res) => {
        showToast({
          message: t('successMessages.deleteMultipleShareableFormSuccess', {
            formLinkDeleted: res.deletedCount,
            totalFormLinks: selected.length,
          }),
          severity: 'success',
          props: { hideToast },
        });
        handleFetchFormsSharableByGeneratedBy();
        setSelectedFormsShareable([]);
      }
    );
  };

  const { showConfirmModal: showDeleteConfirmModal } = useConfirmModal(
    t('pages.shareableFormsPage.confirmDelete'),
    `${t('pages.shareableFormsPage.areYouSure')} ${selectedItem?.id}`,
    async () => RemoveFormShareable(selectedItem.id)
  );

  const ActiveInactiveFormShareable = async (id, isActive) => {
    genericApiToaster(
      activateFormLink,
      id,
      !isActive
        ? t('successMessages.activateShareableFormSuccess')
        : t('successMessages.deactivateShareableFormSuccess'),
      !isActive
        ? t('errorMessages.activateShareableFormError')
        : t('errorMessages.deactivateShareableFormError'),
      showToast,
      hideToast,
      t,
      () => {
        handleFetchFormsSharableByGeneratedBy();
        setSelectedFormsShareable([]);
      },
      () => {}
    );
  };

  const { showConfirmModal: showToggleActiveConfirmModal } = useConfirmModal(
    selectedItem?.isActive
      ? t('pages.shareableFormsPage.confirmDeactivate')
      : t('pages.shareableFormsPage.confirmActivate'),
    `${
      selectedItem?.isActive
        ? t('pages.shareableFormsPage.areYouSureToDeactivate')
        : t('pages.shareableFormsPage.areYouSureToActivate')
    } ${selectedItem?.id}`,
    async () => ActiveInactiveFormShareable(selectedItem.id, selectedItem.isActive)
  );

  const handleOpenFilter = (event) => {
    setFilterPopover(event.currentTarget);
  };

  const handleCloseFilter = () => {
    handleCloseSubFilter();
    setFilterPopover(null);
  };

  const handleCloseSubFilter = () => {
    setSubFilterPopover({
      status: null,
      isMultiple: null,
      language: null,
      isAnonymous: null,
    });
  };

  const handleFilterByName = (event) => {
    setPage(0);
    setSearchValue(event.target.value);
  };

  const handleRemoveSelected = () => {
    setSelectedFormsShareable([]);
  };

  const handleOpenSubFilter = (event, category) => {
    if (category === 'status') {
      setSubFilterPopover({ status: event.currentTarget });
    } else if (category === 'isMultipleSubmission') {
      setSubFilterPopover({ isMultiple: event.currentTarget });
    } else if (category === 'language') {
      setSubFilterPopover({ language: event.currentTarget });
    } else if (category === 'isAnonymous') {
      setSubFilterPopover({ isAnonymous: event.currentTarget });
    } else if (category === 'isHidden') {
      setSubFilterPopover({ isHidden: event.currentTarget }); // FIXED: Opens survey sub-popper
    }
  };

  const handleOpenShareableFormDetails = (row) => {
    setSelectedItem(row);
    setOpenShareableFormDetails(true);
  };

  useEffect(() => {
    const newSelectdsId = currentPageFormsShareable.map((n) => n.id);
    if (newSelectdsId.every((id) => selectedFormsShareable.includes(id))) {
      setIndeterminateCheckbox(true);
    } else setIndeterminateCheckbox(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFormsShareable]);

  const handleTabChange = (event, newTab) => {
    setActiveTab(newTab);
  };

  return (
    <>
      {/* Tabs for Active and Archived Users */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <TabContext value={activeTab}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <TabList onChange={handleTabChange}>
              <Tab
                label={`${t('pages.tabTitles.shareableForms')}`}
                value="1"
                sx={{
                  fontSize: isSmallScreen && '0.65rem',
                }}
              />
              <Tab
                label={`${t('pages.tabTitles.surveyDashboard')} `}
                value="2"
                sx={{
                  fontSize: isSmallScreen && '0.65rem',
                }}
              />
            </TabList>
          </Box>

          <TabPanel value="1" index={0}>
            <Helmet>
              <title> {t('nav.ShareableForms')} </title>
            </Helmet>

            <Box>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                mb={isSmallScreen ? 2 : 5}
              >
                <Typography
                  style={isSmallScreen ? titleHeadingStyleSmole : titleHeadingStyle}
                  mx={1}
                >
                  {t('nav.ShareableForms')}
                </Typography>
                <Grid>
                  {canCreate && (
                    <ActionButton
                      id="add-user-btn"
                      onClick={() =>
                        showModal({
                          title: t('pages.shareableFormsPage.addNewSurvey'),
                          Component: AddShareableForm,
                          props: {
                            hideModal,
                            handleFetchFormsSharableByGeneratedBy,
                            mode: 'survey',
                          },
                        })
                      }
                      icon={isSmallScreen ? 'octicon:plus-circle-16' : 'eva:plus-fill'}
                      text={t('buttons.addNewSurvey')}
                      title={t('buttons.addNewSurvey')}
                      sx={{ mr: 1 }}
                    />
                  )}
                  {canCreate && (
                    <ActionButton
                      id="add-user-btn"
                      onClick={() =>
                        showModal({
                          title: t('pages.shareableFormsPage.addNewShareableForm'),
                          Component: AddShareableForm,
                          props: {
                            hideModal,
                            handleFetchFormsSharableByGeneratedBy,
                            mode: 'shareableForm',
                          },
                        })
                      }
                      icon={isSmallScreen ? 'octicon:plus-circle-16' : 'eva:plus-fill'}
                      text={t('buttons.addNewShareableForm')}
                      title={t('buttons.addNewShareableForm')}
                    />
                  )}
                </Grid>
              </Stack>
              <Card>
                <TableToolbar
                  numSelected={selectedFormsShareable.length}
                  filterName={searchValue} // FIXED: Binds free-text search text
                  filterBy={searchBy} // Binds search target (id, form, generatedBy)
                  setFilterBy={setSearchBy} // Binds searchBy updater
                  handleCloseFilter={handleCloseFilter}
                  handleOpenFilter={handleOpenFilter}
                  onFilterName={handleFilterByName}
                  selected={selectedFormsShareable}
                  handleRemoveSelected={handleRemoveSelected}
                  deleteFunc={multipleFormShareableDelete}
                  model={t(`nav.ShareableForms`)}
                  setFilterName={setSearchValue} // Binds text-input updater
                  filterValue={filterTitle} // Binds active category filter display (Chip)
                  setFilterValue={setFilterTitle} // Binds active category filter clear
                  setPage={setPage}
                />

                <ShareableFormDetails
                  open={openShareableFormDetails}
                  setOpen={setOpenShareableFormDetails}
                  selectedRow={selectedItem}
                />

                <Box
                  sx={{
                    overflowX: 'auto',
                    maxWidth: '100%',
                  }}
                >
                  <TableContainer
                    component={Paper}
                    sx={{
                      '&::-webkit-scrollbar': {
                        width: '6px',
                        height: '6px',
                      },
                      '&::-webkit-scrollbar-track': {
                        backgroundColor: 'transparent',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        backgroundColor: isDark
                          ? 'rgba(255, 255, 255, 0.15)'
                          : 'rgba(0, 0, 0, 0.15)',
                        borderRadius: '10px',
                        '&:hover': {
                          backgroundColor: isDark
                            ? 'rgba(255, 255, 255, 0.3)'
                            : 'rgba(0, 0, 0, 0.3)',
                        },
                      },
                    }}
                  >
                    <Table size={isSmallScreen ? 'small' : 'medium'}>
                      <TableHeaders
                        order={order}
                        orderBy={orderBy}
                        headLabel={TABLE_HEAD_FORMS_SHAREABLE}
                        rowCount={filteredFormsShareable.length}
                        numSelected={selectedFormsShareable.length}
                        onRequestSort={handleRequestSort}
                        onSelectAllClick={handleSelectAllClick}
                        showCheckBox
                        indeterminate={indeterminateCheckbox}
                      />

                      <TableBodyShareableForm
                        currentPageFormsShareable={currentPageFormsShareable}
                        isSelected={isSelected}
                        handleClick={handleClick}
                        handleOpenMenu={handleOpenMenu}
                        emptyRows={emptyRows}
                        isNotFound={isNotFound}
                        filterName={searchValue}
                        onRowClick={handleOpenShareableFormDetails}
                        setActiveTab={setActiveTab}
                        setSelectedFormId={setSelectedFormId}
                      />
                    </Table>
                  </TableContainer>
                </Box>

                {/* Table Pagination */}
                <TablePaginations
                  filteredEntity={filteredFormsShareable}
                  handleChangePage={handleChangePage}
                  handleChangeRowsPerPage={handleChangeRowsPerPage}
                  page={page}
                  rowsPerPage={rowsPerPage}
                  rowsPerPageOptions={[5, 10, 25]}
                />
              </Card>

              {/* menu popover */}
              <Popover
                open={Boolean(popoverAnchorEl)}
                anchorEl={popoverAnchorEl}
                onClose={handleCloseMenu}
                anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{
                  sx: {
                    p: 1,
                    minwidth: 140,
                    '& .MuiMenuItem-root': {
                      px: 1,
                      typography: 'body2',
                      borderRadius: 0.75,
                    },
                  },
                }}
              >
                <MenuItem
                  onClick={() => {
                    setOpenShareableFormDetails(true);
                    handleCloseMenu();
                  }}
                >
                  <Iconify
                    icon="eva:plus-circle-outline"
                    sx={{
                      mr: isSmallScreen ? 1.5 : 2,
                      width: isSmallScreen ? 16 : 20,
                      height: isSmallScreen ? 16 : 20,
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: isSmallScreen && '0.7rem',
                    }}
                  >
                    {t('popover.moreDetails')}
                  </Typography>
                </MenuItem>

                {selectedItem?.isHidden && (
                  <MenuItem
                    onClick={() => {
                      setActiveTab('2');
                      setSelectedFormId(selectedItem);
                      handleCloseMenu();
                    }}
                  >
                    <Iconify
                      icon="eva:bar-chart-outline"
                      sx={{
                        mr: isSmallScreen ? 1.5 : 2,
                        width: isSmallScreen ? 16 : 20,
                        height: isSmallScreen ? 16 : 20,
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: isSmallScreen && '0.7rem',
                      }}
                    >
                      {t('popover.dashboardSurvey')}
                    </Typography>
                  </MenuItem>
                )}
                {canDelete && (
                  <MenuItem
                    sx={{ color: selectedItem?.isActive ? 'error.dark' : 'success.main' }}
                    onClick={() => {
                      showToggleActiveConfirmModal();
                      handleCloseMenu();
                    }}
                  >
                    <Iconify
                      icon={
                        selectedItem?.isActive ? 'eva:lock-outline' : 'eva:unlock-outline'
                      }
                      sx={{
                        mr: isSmallScreen ? 1.5 : 2,
                        width: isSmallScreen ? 16 : 20,
                        height: isSmallScreen ? 16 : 20,
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: isSmallScreen && '0.7rem',
                      }}
                    >
                      {selectedItem?.isActive
                        ? t('popover.deactivate')
                        : t('popover.activate')}
                    </Typography>
                  </MenuItem>
                )}
                {canUpdate && (
                  <MenuItem
                    onClick={() => {
                      showModal({
                        title: selectedItem?.isHidden
                          ? t('surveyDashboard.updateSurvey')
                          : t('pages.shareableFormsPage.updateShareableForm'),
                        Component: AddShareableForm,
                        props: {
                          hideModal,
                          handleFetchFormsSharableByGeneratedBy,
                          row: selectedItem,
                        },
                      });
                      handleCloseMenu();
                    }}
                  >
                    <Iconify
                      icon="eva:edit-fill"
                      sx={{
                        mr: isSmallScreen ? 1.5 : 2,
                        width: isSmallScreen ? 16 : 20,
                        height: isSmallScreen ? 16 : 20,
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: isSmallScreen && '0.7rem',
                      }}
                    >
                      {t('popover.edit')}
                    </Typography>
                  </MenuItem>
                )}
                {canDelete && (
                  <MenuItem
                    sx={{ color: 'error.main' }}
                    onClick={() => {
                      showDeleteConfirmModal();
                      handleCloseMenu();
                    }}
                  >
                    <Iconify
                      icon="eva:trash-2-outline"
                      sx={{
                        mr: isSmallScreen ? 1.5 : 2,
                        width: isSmallScreen ? 16 : 20,
                        height: isSmallScreen ? 16 : 20,
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: isSmallScreen && '0.7rem',
                      }}
                    >
                      {t('popover.delete')}
                    </Typography>
                  </MenuItem>
                )}
              </Popover>

              {/* main popper */}
              <Popper
                open={Boolean(filterPopover)}
                anchorEl={filterPopover}
                placement="left-start"
                onClose={handleCloseFilter}
              >
                <Card
                  sx={{
                    p: 1,
                    minwidth: 140,
                    '& .MuiMenuItem-root': {
                      px: 1,
                      typography: 'body2',
                      borderRadius: 0.75,
                      boxShadow: '0.5px 0.5px 2 px',
                    },
                  }}
                >
                  {/* 1. Language Filter Option */}
                  <MenuItem
                    selected={selectedFilter === 'language'}
                    onMouseEnter={(e) => handleOpenSubFilter(e, 'language')}
                    onTouchStart={(e) => handleOpenSubFilter(e, 'language')}
                    onClick={(e) => handleOpenSubFilter(e, 'language')}
                  >
                    <Iconify
                      icon="eva:arrow-ios-back-outline"
                      sx={{
                        mr: 1,
                        width: isSmallScreen ? 16 : 20,
                        height: isSmallScreen ? 16 : 20,
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{ fontSize: isSmallScreen && '0.7rem' }}
                    >
                      {t('filters.byLanguage')}
                    </Typography>
                  </MenuItem>

                  {/* 2. Anonymous Filter Option */}
                  <MenuItem
                    selected={selectedFilter === 'isAnonymous'}
                    onMouseEnter={(e) => handleOpenSubFilter(e, 'isAnonymous')}
                    onTouchStart={(e) => handleOpenSubFilter(e, 'isAnonymous')}
                    onClick={(e) => handleOpenSubFilter(e, 'isAnonymous')}
                  >
                    <Iconify
                      icon="eva:arrow-ios-back-outline"
                      sx={{
                        mr: 1,
                        width: isSmallScreen ? 16 : 20,
                        height: isSmallScreen ? 16 : 20,
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{ fontSize: isSmallScreen && '0.7rem' }}
                    >
                      {t('filters.byIsAnonymous')}
                    </Typography>
                  </MenuItem>

                  {/* 3. Survey / Form Type Filter Option */}
                  <MenuItem
                    selected={selectedFilter === 'isHidden'}
                    onMouseEnter={(e) => handleOpenSubFilter(e, 'isHidden')}
                    onTouchStart={(e) => handleOpenSubFilter(e, 'isHidden')}
                    onClick={(e) => handleOpenSubFilter(e, 'isHidden')}
                  >
                    <Iconify
                      icon="eva:arrow-ios-back-outline"
                      sx={{
                        mr: 1,
                        width: isSmallScreen ? 16 : 20,
                        height: isSmallScreen ? 16 : 20,
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{ fontSize: isSmallScreen && '0.7rem' }}
                    >
                      {t('filters.byIsHidden') || 'By Form/Survey Type'}
                    </Typography>
                  </MenuItem>

                  {/* 4. Status Filter Option */}
                  <MenuItem
                    selected={selectedFilter === 'status'}
                    onMouseEnter={(e) => handleOpenSubFilter(e, 'status')}
                    onTouchStart={(e) => handleOpenSubFilter(e, 'status')}
                    onClick={(e) => handleOpenSubFilter(e, 'status')}
                  >
                    <Iconify
                      icon="eva:arrow-ios-back-outline"
                      sx={{
                        mr: 1,
                        width: isSmallScreen ? 16 : 20,
                        height: isSmallScreen ? 16 : 20,
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{ fontSize: isSmallScreen && '0.7rem' }}
                    >
                      {t('filters.byStatus')}
                    </Typography>
                  </MenuItem>

                  {/* 5. Multiple Submission Filter Option */}
                  <MenuItem
                    selected={selectedFilter === 'isMultipleSubmission'}
                    onMouseEnter={(e) => handleOpenSubFilter(e, 'isMultipleSubmission')}
                    onTouchStart={(e) => handleOpenSubFilter(e, 'isMultipleSubmission')}
                    onClick={(e) => handleOpenSubFilter(e, 'isMultipleSubmission')}
                  >
                    <Iconify
                      icon="eva:arrow-ios-back-outline"
                      sx={{
                        mr: 1,
                        width: isSmallScreen ? 16 : 20,
                        height: isSmallScreen ? 16 : 20,
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{ fontSize: isSmallScreen && '0.7rem' }}
                    >
                      {t('filters.byMultipleSubmission')}
                    </Typography>
                  </MenuItem>
                </Card>
              </Popper>

              {/* sub popper status */}
              <Popper
                open={Boolean(subFilterPopover.status)}
                anchorEl={subFilterPopover.status}
                onClose={handleCloseSubFilter}
                placement="left-start"
              >
                <Card
                  sx={{
                    p: 1,
                    minwidth: 140,
                    '& .MuiMenuItem-root': {
                      px: 1,
                      typography: 'body2',
                      borderRadius: 0.75,
                      boxShadow: '0.5px 0.5px 2 px',
                    },
                  }}
                >
                  {['true', 'false'].map((isActive, index) => (
                    <MenuItem
                      key={index}
                      onClick={() => {
                        setFilterBy('isActive');
                        setSearchBy('byStatus');
                        setSelectedFilter('isActive');
                        setFilterTitle(isActive); // FIXED: Feeds directly into filterTitle
                        handleCloseFilter();
                        handleCloseSubFilter();
                        setPage(0);
                      }}
                      onTouchStart={() => {
                        setFilterBy('isActive');
                        setSearchBy('byStatus');
                        setSelectedFilter('isActive');
                        setFilterTitle(isActive); // FIXED: Feeds directly into filterTitle
                        handleCloseFilter();
                        handleCloseSubFilter();
                        setPage(0);
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: isSmallScreen && '0.7rem',
                        }}
                      >
                        {isActive === 'true'
                          ? t('userStatus.active')
                          : t('userStatus.inactive')}
                      </Typography>
                    </MenuItem>
                  ))}
                </Card>
              </Popper>

              {/* sub popper multiple submission */}
              <Popper
                open={Boolean(subFilterPopover.isMultiple)}
                anchorEl={subFilterPopover.isMultiple}
                onClose={handleCloseSubFilter}
                placement="left-start"
              >
                <Card
                  sx={{
                    p: 1,
                    minwidth: 140,
                    '& .MuiMenuItem-root': {
                      px: 1,
                      typography: 'body2',
                      borderRadius: 0.75,
                      boxShadow: '0.5px 0.5px 2 px',
                    },
                  }}
                >
                  {['true', 'false'].map((isMultipleSubmission, index) => (
                    <MenuItem
                      key={index}
                      onClick={() => {
                        setFilterBy('isMultipleSubmission');
                        setSearchBy('byMultipleSubmission');
                        setSelectedFilter('isMultipleSubmission');
                        setFilterTitle(isMultipleSubmission); // FIXED: Feeds directly into filterTitle
                        handleCloseFilter();
                        handleCloseSubFilter();
                        setPage(0);
                      }}
                      onTouchStart={() => {
                        setFilterBy('isMultipleSubmission');
                        setSearchBy('byMultipleSubmission');
                        setSelectedFilter('isMultipleSubmission');
                        setFilterTitle(isMultipleSubmission); // FIXED: Feeds directly into filterTitle
                        handleCloseFilter();
                        handleCloseSubFilter();
                        setPage(0);
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: isSmallScreen && '0.7rem',
                        }}
                      >
                        {isMultipleSubmission === 'true'
                          ? t('isMultipleSubmission.yes')
                          : t('isMultipleSubmission.no')}
                      </Typography>
                    </MenuItem>
                  ))}
                </Card>
              </Popper>

              {/* language sub popover */}
              <Popper
                open={Boolean(subFilterPopover.language)}
                anchorEl={subFilterPopover.language}
                onClose={handleCloseSubFilter}
                placement="left-start"
              >
                <Card
                  sx={{
                    p: 1,
                    minwidth: 140,
                    '& .MuiMenuItem-root': {
                      px: 1,
                      typography: 'body2',
                      borderRadius: 0.75,
                      boxShadow: '0.5px 0.5px 2 px',
                    },
                  }}
                >
                  {LANGS.map((lang, index) => (
                    <MenuItem
                      key={index}
                      onClick={() => {
                        setFilterBy('language');
                        setSearchBy('byLanguage');
                        setSelectedFilter('language');
                        setFilterTitle(lang.value); // FIXED: Feeds directly into filterTitle
                        handleCloseFilter();
                        handleCloseSubFilter();
                        setPage(0);
                      }}
                      onTouchStart={() => {
                        setFilterBy('language');
                        setSearchBy('byLanguage');
                        setSelectedFilter('language');
                        setFilterTitle(lang.value); // FIXED: Feeds directly into filterTitle
                        handleCloseFilter();
                        handleCloseSubFilter();
                        setPage(0);
                      }}
                    >
                      <Stack direction="row" spacing={1}>
                        <img
                          src={lang.icon}
                          alt={lang.value}
                          width={isSmallScreen ? 20 : 28}
                          height={isSmallScreen ? 16 : 20}
                        />
                      </Stack>
                    </MenuItem>
                  ))}
                </Card>
              </Popper>

              {/* sub popper is anonymous */}
              <Popper
                open={Boolean(subFilterPopover.isAnonymous)}
                anchorEl={subFilterPopover.isAnonymous}
                onClose={handleCloseSubFilter}
                placement="left-start"
              >
                <Card
                  sx={{
                    p: 1,
                    minwidth: 140,
                    '& .MuiMenuItem-root': {
                      px: 1,
                      typography: 'body2',
                      borderRadius: 0.75,
                      boxShadow: '0.5px 0.5px 2 px',
                    },
                  }}
                >
                  {['true', 'false'].map((isAnonymous, index) => (
                    <MenuItem
                      key={index}
                      onClick={() => {
                        setFilterBy('isAnonymous');
                        setSearchBy('byIsAnonymous');
                        setSelectedFilter('isAnonymous');
                        setFilterTitle(isAnonymous); // FIXED: Feeds directly into filterTitle
                        handleCloseFilter();
                        handleCloseSubFilter();
                        setPage(0);
                      }}
                      onTouchStart={() => {
                        setFilterBy('isAnonymous');
                        setSearchBy('byIsAnonymous');
                        setSelectedFilter('isAnonymous');
                        setFilterTitle(isAnonymous); // FIXED: Feeds directly into filterTitle
                        handleCloseFilter();
                        handleCloseSubFilter();
                        setPage(0);
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: isSmallScreen && '0.7rem',
                        }}
                      >
                        {isAnonymous === 'true'
                          ? t('isMultipleSubmission.yes')
                          : t('isMultipleSubmission.no')}
                      </Typography>
                    </MenuItem>
                  ))}
                </Card>
              </Popper>

              {/* sub popper is hidden (Survey / Form Type) */}
              <Popper
                open={Boolean(subFilterPopover.isHidden)}
                anchorEl={subFilterPopover.isHidden}
                onClose={handleCloseSubFilter}
                placement="left-start"
              >
                <Card
                  sx={{
                    p: 1,
                    minwidth: 140,
                    '& .MuiMenuItem-root': {
                      px: 1,
                      typography: 'body2',
                      borderRadius: 0.75,
                      boxShadow: '0.5px 0.5px 2 px',
                    },
                  }}
                >
                  {[
                    { value: 'true', display: t('formType.survey') || 'Survey' },
                    {
                      value: 'false',
                      display: t('formType.standard') || 'Standard Form',
                    },
                  ].map((type, index) => (
                    <MenuItem
                      key={index}
                      onClick={() => {
                        setFilterBy('isHidden');
                        setSearchBy('byIsHidden');
                        setSelectedFilter('isHidden');
                        setFilterTitle(type.value); // FIXED: Feeds directly into filterTitle
                        handleCloseFilter();
                        handleCloseSubFilter();
                        setPage(0);
                      }}
                      onTouchStart={() => {
                        setFilterBy('isHidden');
                        setSearchBy('byIsHidden');
                        setSelectedFilter('isHidden');
                        setFilterTitle(type.value); // FIXED: Feeds directly into filterTitle
                        handleCloseFilter();
                        handleCloseSubFilter();
                        setPage(0);
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: isSmallScreen && '0.7rem',
                        }}
                      >
                        {type.display}
                      </Typography>
                    </MenuItem>
                  ))}
                </Card>
              </Popper>
            </Box>
          </TabPanel>

          <TabPanel value="2" index={1}>
            {!selectedFormId && (
              <FormHelperText error>{t('formControl.surveyRequired')}</FormHelperText>
            )}
            {selectedFormId && (
              <Surveydashboard
                selectedFormId={selectedFormId}
                formLinks={shareForms.filter((form) => form.isHidden)}
                onFormLinkChange={setSelectedFormId}
              />
            )}
          </TabPanel>
        </TabContext>
      </Box>
    </>
  );
}

export default ShareableFormsPage;
